import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  INJECTOR,
  signal,
} from '@angular/core';
import { FieldOkComponent } from '@softline/dynamic';
import { ReactiveFormsModule } from '@angular/forms';
import { ConfigPropertyOverrideService, Kasse } from '@softapps/wws/pos-core';
import { PageComponent, PageHeaderComponent } from '@softapps/core/pages';
import { MessageBarService, SwitchComponent } from '@softline/ui-core';
import {
  backNavigation,
  handleRequestError,
  menuItems,
  RemoteConfigStore,
  SOFTLINE_FEATURE_REMOTE_CONFIG,
  title,
} from '@softline/application';
import {
  JwtAuthenticationStore,
  SOFTLINE_FEATURE_JWT_AUTHENTICATION,
} from '@softline/auth';
import { isDefinedNotEmpty, Store } from '@softline/core';
import { Router } from '@angular/router';
import { Configuration } from '../../types/config';
import { WorkstationConfigService } from '@softapps/wws/pos-core';
import { SOFTLINE_CONST_WWS_POS_DISPLAY_CONFIG_KEY } from '../../pos-display.shared';

const MODULE = 'pos-display';

@Component({
  selector: 'soft-display-config',
  imports: [
    FieldOkComponent,
    ReactiveFormsModule,
    PageComponent,
    PageHeaderComponent,
    SwitchComponent,
  ],
  templateUrl: './config.component.html',
  styleUrl: './config.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class ConfigComponent {
  readonly #store = inject(Store);
  readonly #router = inject(Router);
  readonly #injector = inject(INJECTOR);
  readonly messageBarService = inject(MessageBarService);
  readonly #workstationDisplayService = inject(WorkstationConfigService);
  readonly overrides = inject(ConfigPropertyOverrideService);

  readonly workstation = signal<Kasse | undefined>(undefined);

  // WebAppVw properties for the pos-display module.
  // Only the base `data` getter is registered, so dig into the module ourselves.
  readonly #remoteConfig = this.#store.signal(
    SOFTLINE_FEATURE_REMOTE_CONFIG,
    RemoteConfigStore.getters.data
  );
  readonly properties = computed(() =>
    (
      this.#remoteConfig()?.modules?.find((m) => m.module === MODULE)?.properties ?? []
    ).map((p) => ({
      key: p.key,
      value: p.value,
      isBoolean: this.overrides.isBoolean(p.value),
      checked: this.overrides.resolve(MODULE, p.key, p.value),
    }))
  );

  toggle(key: string, checked: boolean): void {
    this.overrides.set(MODULE, key, checked);
  }

  readonly isValueValid = computed(() => {
    return isDefinedNotEmpty(this.workstation());
  });

  constructor() {
    backNavigation('/');
    title('Kassendisplay Konfigurieren');
    void this.loadConfiguration();
    // Settings panel isn't behind the RemoteConfigResolver, so fetch it here.
    void this.#store.dispatch(
      SOFTLINE_FEATURE_REMOTE_CONFIG,
      RemoteConfigStore.actions.loadOnce
    );
    menuItems(() => {
      return [
        {
          name: 'pos-display/config/workstation/submit',
          title: 'Speichern',
          outlet: 'responsive',
          class: 'soft-button primary m-2',
          type: 'execute',
          icon: 'fa-regular fa-save',
          canExecute: this.isValueValid(),
          execute: () => this.onSubmit(),
        },
        {
          name: 'pos-display/config/workstation',
          title: 'Kassendisplay Konfigurieren',
          icon: 'fa-regular fa-gear',
          type: 'route',
          outlet: 'nav',
          routerLink: ['/pos-display/config'],
        },
      ];
    });
  }

  async loadConfiguration() {
    try {
      const config = await this.#workstationDisplayService.load<Configuration>(
        SOFTLINE_CONST_WWS_POS_DISPLAY_CONFIG_KEY
      );
      if (config?.name) this.workstation.set(config.name);
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  async saveConfiguration() {
    const value = this.workstation();

    if (!value) return;

    const config: Configuration = {
      name: value,
    };

    await this.#workstationDisplayService.save<Configuration>(
      SOFTLINE_CONST_WWS_POS_DISPLAY_CONFIG_KEY,
      config
    );
  }

  async onSubmit() {
    try {
      await this.saveConfiguration();
      await this.#store.dispatch(
        SOFTLINE_FEATURE_JWT_AUTHENTICATION,
        JwtAuthenticationStore.actions.change,
        // FIXME: firmaid is hardcoded to 2 here but to 1 in pos-display.providers.ts —
        // see there. Confirm the correct value, then make it configurable.
        { workstation: this.workstation()?.kassennummer, firmaid: 2 }
      );

      this.messageBarService.success('Die Konfiguration wurde erfolgreich gespeichert.');
      await this.#router.navigate(['/']);
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }
}
